import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

const screens = [
  {
    id: 'welcome',
    palette: '',
    icon: 'fa-heart',
    title: 'Welcome to INTIVIV™',
    body: 'Your private, judgment-free intimate wellness companion — AI-guided, gynaecologist-backed.',
    cta: 'Get Started',
  },
  {
    id: 'vivi',
    palette: 'palette-rose',
    icon: 'fa-comment-dots',
    title: 'Meet VIVI',
    body: 'Your personal AI intimate wellness coach, available 24/7. Ask anything — VIVI listens, guides, and never judges.',
    cta: 'Continue',
  },
  {
    id: 'assessments',
    palette: 'palette-teal',
    icon: 'fa-clipboard-check',
    title: 'Know Your Wellness Score',
    body: '16 clinically-designed assessments to understand your intimate health baseline and track your progress over time.',
    cta: 'Continue',
  },
  {
    id: 'privacy',
    palette: '',
    icon: 'fa-shield-halved',
    title: 'Your Privacy is Sacred',
    body: 'End-to-end encrypted conversations. Data never shared. One-tap deletion. Biometric lock optional. You are always in control.',
    cta: "Let's Begin",
  },
];

export default function Onboarding() {
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();
  const screen = screens[idx];

  const next = () => {
    if (idx < screens.length - 1) { setIdx(i => i + 1); }
    else {
      localStorage.setItem('intiviv_onboarding_done', '1');
      navigate('/home');
    }
  };

  return (
    <div className={`onboarding-shell ${screen.palette}`} style={{ background: screen.palette === 'palette-rose' ? '#1A0A14' : screen.palette === 'palette-teal' ? '#0A1A1A' : '#0A0A0A' }}>
      <div className="onboard-glow" style={{ top: '-100px', right: '-100px' }} />
      <div className="onboard-glow" style={{ bottom: '-100px', left: '-100px' }} />

      {/* Progress dots */}
      <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6, zIndex: 10 }}>
        {screens.map((_, i) => (
          <div key={i} style={{
            width: i === idx ? 20 : 6, height: 6,
            background: i === idx ? 'var(--gold)' : 'var(--bg3)',
            borderRadius: 3, transition: 'all 0.3s',
          }} />
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            padding: '80px 32px 40px', textAlign: 'center',
            position: 'relative', zIndex: 1,
          }}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            style={{
              width: 110, height: 110, borderRadius: '50%',
              background: 'var(--gold-dim)',
              border: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 32, color: 'var(--gold)',
              overflow: 'hidden',
              boxShadow: '0 0 24px rgba(212,175,55,0.25)',
            }}
          >
            {screen.id === 'welcome' ? (
              <img
                src="/intiviv-logo.png?v=4"
                alt="INTIVIV"
                style={{
                  width: '78%',
                  height: '78%',
                  objectFit: 'contain',
                  display: 'block',
                  filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.4))',
                }}
              />
            ) : (
              <i className={`fa-solid ${screen.icon}`} style={{ fontSize: '2.7rem' }} />
            )}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            style={{ fontFamily: 'var(--font-display)', fontSize: '2.5rem', marginBottom: 16, lineHeight: 1.2 }}
          >
            {screen.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{ color: 'var(--text-muted)', fontSize: '0.98rem', lineHeight: 1.75, maxWidth: 320, marginBottom: 48 }}
          >
            {screen.body}
          </motion.p>

          {user && idx === 0 && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
              style={{ fontSize: '0.89rem', color: 'var(--gold)', marginBottom: 16 }}>
              Welcome, {user.name.split(' ')[0]}
            </motion.p>
          )}
        </motion.div>
      </AnimatePresence>

      <div style={{ padding: '0 24px 48px', position: 'relative', zIndex: 1 }}>
        <button className="btn-gold w-full" onClick={next} style={{ fontSize: '0.89rem' }}>
          {screen.cta}
        </button>
        {idx < screens.length - 1 && (
          <button
            onClick={() => { localStorage.setItem('intiviv_onboarding_done', '1'); navigate('/home'); }}
            style={{ width: '100%', textAlign: 'center', marginTop: 14, fontSize: '0.79rem', color: 'var(--text-dim)', background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.1em' }}
          >
            SKIP
          </button>
        )}
      </div>
    </div>
  );
}
